import type { GetCurrentOpenInterestRequest, GetCurrentOpenInterestResponse } from '../../proto/generated/candle-data-depot'
import type { TradingSession } from '../../proto/generated/trading-calendar'
import type { CalendarData } from '../chart'

/**
 * IndicatorUtils must not import util/chart or the API clients: they pull in the store/React, and IndicatorUtils is
 * also bundled into the indicator Web Worker. The main thread registers these implementations at load (util/chart);
 * inside the worker they stay unset and throw, which sends that indicator back to the main thread.
 */
type TradingSessionsResolver = (timeInSecondsInIst: number, calendarData: CalendarData) => TradingSession[]
type OpenInterestFetcher = (request: GetCurrentOpenInterestRequest) => Promise<GetCurrentOpenInterestResponse>

let tradingSessionsResolver: TradingSessionsResolver | undefined
let openInterestFetcher: OpenInterestFetcher | undefined
/** Some indicator code catches the error and carries on with NaN (e.g. VWAP), so a request is also recorded here. */
let missingBindingRequested = false

/** True if an unregistered binding was requested since the last call; resets the flag. */
export function takeMissingBindingRequested(): boolean {
    const requested = missingBindingRequested
    missingBindingRequested = false
    return requested
}

export function registerIndicatorMainThreadBindings(bindings: {
    getTradingSessionsForDate: TradingSessionsResolver
    getCurrentOpenInterests: OpenInterestFetcher
}) {
    tradingSessionsResolver = bindings.getTradingSessionsForDate
    openInterestFetcher = bindings.getCurrentOpenInterests
}

export function getTradingSessionsForDate(timeInSecondsInIst: number, calendarData: CalendarData): TradingSession[] {
    if (!tradingSessionsResolver) {
        missingBindingRequested = true
        throw new Error('Trading sessions are not available in this context')
    }
    return tradingSessionsResolver(timeInSecondsInIst, calendarData)
}

export function getCurrentOpenInterests(request: GetCurrentOpenInterestRequest): Promise<GetCurrentOpenInterestResponse> {
    if (!openInterestFetcher) {
        missingBindingRequested = true
        return Promise.reject(new Error('Open interest is not available in this context'))
    }
    return openInterestFetcher(request)
}
